import React from 'react';
import { Icon } from '@noths/polaris-design-system-core-components';

import { MAX_RATING, ratingLabel } from '../../../utils/formatRating';
import type { StarRatingProps } from './types';
import * as styles from './StarRating.styles';

/* Built once rather than per render: a row of review cards draws up to five of these each,
   and the descriptors are identical every time. */
const STARS = Array.from({ length: MAX_RATING }, (_, index) => (
  <Icon aria-hidden key={index} name="starFull" size="sm" />
));

/**
 * A review's rating, as filled stars.
 *
 * Only the earned stars are drawn: a 4 rating is four stars, with no fifth outline
 * behind it, which is how the reviews designs show it. The row is one `img` with the
 * whole sentence as its label, so it announces as "Rated 4.5 out of 5" rather than as
 * four or five separate graphics.
 *
 * The label keeps the unrounded rating while the stars round to the nearest whole one,
 * so a 4.5 is not misreported as a 5 to a screen reader.
 */
export const StarRating = ({ rating }: StarRatingProps) => {
  if (!Number.isFinite(rating)) {
    return null;
  }

  const starCount = Math.min(MAX_RATING, Math.max(0, Math.round(rating)));

  return (
    <span aria-label={ratingLabel(rating)} css={styles.stars} data-testid="star-rating" role="img">
      {STARS.slice(0, starCount)}
    </span>
  );
};

StarRating.displayName = 'StarRating';
